/**
 * Integraciones con puntos de venta (TPV).
 *
 * Decision del 4 sep 2026 (Pol y Alex): se empieza por los dos TPV con API
 * de autoservicio, Loyverse (Mexico y resto) y Square (Espanya y EE. UU.).
 * El resto de la lista de Alex (Revo, Agora, Parrot, Soft Restaurant, Toast)
 * sigue entrando por el importador de archivos.
 *
 * 18 sep 2026: Cegid acepta la solicitud de API de Revo XEF y se anyade como
 * tercer proveedor (OAuth 2.0 con PKCE, integracion publica).
 *
 * Lo que se lee del TPV es lo mismo que traga el importador: la venta del dia
 * por metodo de cobro y plataforma, y las unidades vendidas por plato. Nada
 * mas. Estos tipos son los que viajan entre la API y la web; los tokens de
 * acceso NUNCA salen de la API.
 */

export type PosProvider = 'loyverse' | 'square' | 'revo' | 'glop' | 'lastapp' | 'restaurantpe' | 'bistrosoft' | 'polotab' | 'sumup';

export const POS_PROVIDERS: PosProvider[] = ['loyverse', 'square', 'revo', 'glop', 'lastapp', 'restaurantpe', 'bistrosoft', 'polotab', 'sumup'];

/**
 * Proveedores en pruebas: la web los anuncia como «Próximamente» donde no estan configurados
 * (o ya conectados). Revo sale de aqui cuando Cegid certifique la integracion
 * y de el Client ID de produccion.
 */
export const POS_PROVIDERS_PREVIEW: PosProvider[] = ['revo', 'lastapp'];

export const POS_PROVIDER_LABELS: Record<PosProvider, string> = {
  loyverse: 'Loyverse',
  square: 'Square',
  revo: 'Revo XEF',
  glop: 'GLOP',
  lastapp: 'Last.app',
  restaurantpe: 'Restaurant.pe',
  bistrosoft: 'Bistrosoft',
  polotab: 'PoloTab',
  sumup: 'SumUp',
};

/**
 * Países donde funciona cada punto de venta (bloque C, 14 oct 2026). En
 * Configuración salen primero los del país de la cuenta; los demás siguen
 * ahí (un local puede estar en otro país). Vacío = en cualquier país.
 */
export const POS_PROVIDER_COUNTRIES: Record<PosProvider, readonly string[]> = {
  loyverse: [],
  square: ['US', 'ES', 'CA', 'GB', 'IE', 'FR', 'AU', 'JP'],
  revo: ['ES', 'MX', 'AD', 'PT'],
  glop: ['ES', 'AD'],
  lastapp: ['ES', 'PT', 'MX'],
  restaurantpe: ['PE', 'CO', 'MX', 'CL', 'EC', 'BO'],
  bistrosoft: ['AR', 'UY', 'CL', 'PY'],
  polotab: ['MX', 'CO'],
  sumup: ['ES', 'PT', 'FR', 'IT', 'DE', 'GB', 'IE', 'CL', 'BR', 'US'],
};

/** ¿Funciona este punto de venta en el país? (sin lista = en todos). */
export function posEnPais(provider: PosProvider, country: string | undefined): boolean {
  const lista = POS_PROVIDER_COUNTRIES[provider];
  return lista.length === 0 || lista.includes((country ?? '').toUpperCase());
}

/**
 * Puntos de venta ordenados para un país: primero los que funcionan allí
 * (los de lista corta de países antes que los de todo el mundo), después el
 * resto, cada tramo en el orden de `POS_PROVIDERS`.
 */
export function posOrdenadosPorPais(country: string | undefined): PosProvider[] {
  const peso = (p: PosProvider): number => {
    if (!posEnPais(p, country)) return 2;
    return POS_PROVIDER_COUNTRIES[p].length ? 0 : 1;
  };
  return [...POS_PROVIDERS].sort((a, b) => peso(a) - peso(b) || POS_PROVIDERS.indexOf(a) - POS_PROVIDERS.indexOf(b));
}

export type PosIntegrationStatus = 'connected' | 'error';

/** Una tienda del TPV y a que local nuestro va a parar. */
export interface PosStoreMapping {
  /** Id de la tienda en el TPV (store_id de Loyverse, location_id de Square; en Revo, la cuenta). */
  externalId: string;
  externalName: string;
  /** Local de Alexia. `null` = esta tienda se ignora. */
  locationId: string | null;
  /** Zona horaria que declara el TPV para esa tienda (Square la da; Loyverse no). */
  timezone?: string;
}

/** Lo que la web ve de una conexion. Sin tokens. */
export interface PosIntegrationView {
  provider: PosProvider;
  /**
   * Id de la conexion dentro del tenant (7 sep 2026). Un grupo con dos
   * restaurantes en dos cuentas distintas del mismo TPV tiene dos conexiones
   * del mismo proveedor; cada una va a su local. Las conexiones anteriores a
   * esta fecha llevan como id el nombre del proveedor.
   */
  connectionId: string;
  status: PosIntegrationStatus;
  /** Nombre del negocio en el TPV, para que el usuario reconozca la cuenta. */
  merchantName?: string;
  merchantId?: string;
  /**
   * Moneda en la que trabaja el TPV (ISO 4217). Alexia copia los importes
   * tal cual, sin convertir: si no coincide con la del restaurante, la web
   * avisa. (Pol lo vio el primer dia: Loyverse en USD y Alexia en EUR.)
   */
  merchantCurrency?: string;
  /** 'oauth' = conectado con el flujo del proveedor; 'token' = token pegado a mano (Loyverse). */
  authType: 'oauth' | 'token';
  connectedAt: string;
  connectedBy?: string;
  stores: PosStoreMapping[];
  /**
   * Zona horaria por defecto para convertir la hora del ticket en fecha de
   * negocio, cuando la tienda no declara la suya.
   */
  timezone: string;
  /**
   * Hora a la que empieza el dia de negocio (0-23). Un ticket de las 01:30
   * con corte a las 5 cuenta para el dia anterior. Por defecto 5.
   */
  businessDayStartHour: number;
  /**
   * Nombre de producto del TPV (normalizado) → plato de la carta. Solo lo que
   * el usuario ha emparejado a mano; el resto se empareja por nombre.
   */
  itemMappings: Record<string, string>;
  lastSyncAt?: string;
  /**
   * Último día (YYYY-MM-DD) con ventas que trajo este punto de venta (revisión
   * del 14 oct 2026). `lastSyncAt` es cuándo se sincronizó, no si trajo ventas:
   * Configuración decía «trajo ventas hoy» con 13 días sin anotar en Ventas.
   * Ausente en conexiones anteriores hasta su siguiente sincronización.
   */
  lastSaleDate?: string;
  /** Ultimo rango sincronizado, para que la web proponga continuar desde ahi. */
  lastSyncRange?: { start: string; end: string };
  lastSyncError?: string;
  /**
   * La ultima sincronizacion entro, pero salto tickets que no se podian
   * importar (Kilo, oct 2026). Se guarda para que la nocturna tambien avise.
   */
  lastSyncWarning?: string;
}

export interface PosProviderAvailability {
  /** ¿Estan las credenciales de la app configuradas en este entorno? */
  oauth: boolean;
  /** ¿Está disponible la conexión sin OAuth (token, credenciales o cuenta habilitada)? */
  token: boolean;
}

export interface PosIntegrationsResponse {
  items: PosIntegrationView[];
  providers: Record<PosProvider, PosProviderAvailability>;
}

/** Cambios que el usuario puede hacer sobre una conexion. */
export interface PosIntegrationPatch {
  stores?: Array<Pick<PosStoreMapping, 'externalId' | 'locationId'>>;
  timezone?: string;
  businessDayStartHour?: number;
  /** Se FUNDE con lo que hubiera; `null` borra un emparejamiento. */
  itemMappings?: Record<string, string | null>;
}

export interface PosSyncResult {
  provider: PosProvider;
  connectionId: string;
  start: string;
  end: string;
  /** Tickets leidos del TPV (ventas y devoluciones). */
  receipts: number;
  /** Dias de venta escritos (por local). */
  days: number;
  /** Tiendas del TPV sin local asignado que se han saltado. */
  skippedStores: string[];
  /** Tickets que el TPV devolvio pero no se han podido importar. */
  skippedSalesCount?: number;
  /** Los primeros, para ensenarlos y buscarlos en el TPV. */
  skippedSales?: Array<{ externalId: string; date: string; document?: string; reason: string }>;
}

export interface PosUnmatchedItem {
  /** Nombre tal y como lo llama el TPV. */
  name: string;
  units: number;
}

export interface PosUnitsResult {
  provider: PosProvider;
  start: string;
  end: string;
  /** Platos de la carta a los que se les han puesto unidades. */
  matched: Array<{ menuItemId: string; menuItemName: string; units: number; fuzzy: boolean }>;
  /** Productos del TPV que no se han podido emparejar con ningun plato. */
  unmatched: PosUnmatchedItem[];
}

/**
 * Zona horaria por defecto segun el pais del restaurante. Se puede cambiar
 * en la conexion; esto solo evita que un mexicano tenga que buscar
 * «America/Mexico_City» en un desplegable el primer dia.
 */
export const POS_DEFAULT_TIMEZONE: Record<string, string> = {
  ES: 'Europe/Madrid',
  MX: 'America/Mexico_City',
  CO: 'America/Bogota',
  AR: 'America/Argentina/Buenos_Aires',
  CL: 'America/Santiago',
  PE: 'America/Lima',
  US: 'America/New_York',
};

export function defaultPosTimezone(country: string | undefined): string {
  return (country && POS_DEFAULT_TIMEZONE[country]) || 'UTC';
}
